import { forwardRef } from "react";
import { IMaskInput } from "react-imask";
import { cn } from "../lib/cn";
import { useFieldControlProps } from "./Field";

export interface NinoInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "size" | "value" | "onChange"
  > {
  /** Unformatted NINO string in/out, e.g. "QQ123456C". */
  value?: string;
  /** Called with the unmasked value on every change. */
  onChange?: (value: string) => void;
  /** Error state — error border + aria-invalid. */
  invalid?: boolean;
}

/** Class string copied verbatim from the Input atom (bare input, size md). */
const inputClasses =
  "w-full rounded-lg border border-border-control bg-surface-default px-3 transition-colors " +
  "outline-none focus-visible:border-border-focused focus-visible:ring-2 focus-visible:ring-border-focused-subtle " +
  "h-10 type-body-lg text-content-primary placeholder:text-content-disabled disabled:cursor-not-allowed disabled:opacity-38";

const invalidClasses =
  "border-surface-error focus-visible:border-surface-error";

/** imask has no default `A` definition — register the uppercase letter class. */
const ninoDefinitions = { A: /[A-Z]/ };

/** UK National Insurance number input — uppercase transform on write. */
export const NinoInput = forwardRef<HTMLInputElement, NinoInputProps>(
  (
    {
      className,
      value,
      onChange,
      invalid = false,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...props
    },
    ref,
  ) => {
    const fieldControlProps = useFieldControlProps({
      id,
      describedBy: ariaDescribedBy,
      invalid: ariaInvalid || invalid,
      required: ariaRequired,
    });
    const resolvedInvalid = Boolean(fieldControlProps["aria-invalid"]);

    return (
      <IMaskInput
        inputRef={ref}
        data-slot="nino-input"
        {...fieldControlProps}
        mask="AA 00 00 00 A"
        definitions={ninoDefinitions}
        prepare={(chars: string) => chars.toUpperCase()}
        unmask={true}
        value={value}
        onAccept={(accepted: string) => {
          onChange?.(accepted);
        }}
        placeholder="QQ 12 34 56 C"
        className={cn(
          inputClasses,
          resolvedInvalid && invalidClasses,
          className,
        )}
        {...props}
      />
    );
  },
);
NinoInput.displayName = "NinoInput";
